import { get, del, post, put } from 'aws-amplify/api';
import { format } from 'date-fns';
import { News } from '../models/news.model';
import { CreateNewsRequest, CreateNewsRequestSchema } from '../models/createNews.model';
import { replaceNullsWithEmpty } from '../utils/utils';

const DISPLAY_DATE_ISO = /^\d{4}-\d{2}-\d{2}$/;

export const createNews = async (createNews: CreateNewsRequest) => {
  const createNewsBody = mapToRequest(createNews);
  const response = await post({
    apiName: 'news',
    path: '/news',
    options: { body: createNewsBody },
  }).response;

  const json = (await response.body.json()) as any;
  return json;
};

export const getNewsList = async (filters: {
  limit?: number;
  lastKey?: string;
  division?: string;
}): Promise<{ items: News[]; lastKey: string }> => {
  const limit = filters.limit ?? 100;
  const lastKey = filters.lastKey ?? '';
  const division = filters.division;

  let path = `/news?limit=${limit}`;
  if (lastKey) {
    path += `&lastKey=${lastKey}`;
  }
  if (division) {
    path += `&division=${division}`;
  }

  const response = await get({
    apiName: 'news',
    path,
  }).response;
  const json = (await response.body.json()) as any;
  return {
    items: json.items.map((a: any) => mapFromResponse(a)),
    lastKey: json.lastKey,
  };
};

export const getNews = async (newsId: string): Promise<News> => {
  const response = await get({ apiName: 'news', path: `/news/${newsId}` }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
};

export const updateNews = async (news: News): Promise<void> => {
  // For updates, we send the full news object (excluding id which is in the path)
  const { id, ...updateData } = news;
  await put({
    apiName: 'news',
    path: `/news/${news.id}`,
    options: { body: updateData },
  }).response;
};

export const deleteNews = async (newsId: string): Promise<void> => {
  await del({ apiName: 'news', path: `/news/${newsId}` }).response;
};

const mapFromResponse = (news: any): News => {
  const res = replaceNullsWithEmpty({ ...news }) as News;
  const displayDate =
    typeof res.displayDate === 'string' && DISPLAY_DATE_ISO.test(res.displayDate)
      ? res.displayDate
      : format(res.createdAt, 'yyyy-MM-dd');
  return { ...res, displayDate };
};

const mapToRequest = (news: CreateNewsRequest): any => {
  const resParse = CreateNewsRequestSchema.safeParse(news);
  if (resParse.success) {
    return resParse.data;
  }

  throw new Error('Validation error');
};
